import type { ReactNode } from "react";
import { Label } from "@/components/ui/Input";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { PublicStationOption } from "@/lib/zplPrintLocationMatch";
import { cn } from "@/lib/utils";

export const ZPL_LOCATION_SELECT_HOVER_HINT =
  "Pick a station. Hub sends the label to the printer at that station with the right stock and DPI.";

export function ZplLocationSelect({
  id,
  label = "Location",
  showLabel = true,
  size = "default",
  value,
  onChange,
  disabled = false,
  className,
  triggerClassName,
  triggerMode = "labeled",
  triggerIcon,
  ariaSummary,
  onListOpenChange,
  unavailableMessage,
  hoverHint = ZPL_LOCATION_SELECT_HOVER_HINT,
  siteCode,
}: {
  id: string;
  label?: string;
  showLabel?: boolean;
  size?: "compact" | "default" | "touch";
  value: string;
  onChange: (station: string) => void;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  triggerMode?: "labeled" | "icon";
  triggerIcon?: ReactNode;
  ariaSummary?: string;
  onListOpenChange?: (open: boolean) => void;
  unavailableMessage?: string | null;
  hoverHint?: string;
  /** When set, only stations at this site are listed. The current value stays visible. */
  siteCode?: string;
}): JSX.Element {
  const stationsQuery = useQuery({
    queryKey: ["public-stations"],
    queryFn: () => apiFetch<{ stations: PublicStationOption[] }>("/api/zpl-printers/stations"),
    staleTime: 120_000,
  });
  const stations = (stationsQuery.data?.stations ?? []).filter((station) => {
    if (siteCode == null || siteCode === "") return true;
    if (station.siteCode === siteCode) return true;
    return String(station.id) === value;
  });
  const nameCounts = new Map<string, number>();
  for (const station of stations) {
    nameCounts.set(station.name, (nameCounts.get(station.name) ?? 0) + 1);
  }

  const labelClassName =
    size === "touch"
      ? "text-[10px] font-semibold uppercase tracking-wide text-slate-500"
      : undefined;

  return (
    <div className={cn("flex flex-col items-stretch gap-0.5", className)}>
      {showLabel ? (
        <div className="flex items-center gap-1">
          <Label htmlFor={id} className={labelClassName}>
            {label}
          </Label>
          {hoverHint !== "" ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
        </div>
      ) : null}
      <HubTouchSelect
        id={id}
        aria-label={showLabel ? undefined : ariaSummary?.trim() !== "" ? ariaSummary : label}
        size={size}
        value={value}
        onChange={onChange}
        placeholder={label}
        options={stations.map((station) => ({
          value: String(station.id),
          label:
            (nameCounts.get(station.name) ?? 0) > 1 && station.siteCode
              ? `${station.name} (${station.siteCode})`
              : station.name,
        }))}
        disabled={disabled}
        triggerClassName={triggerClassName}
        triggerMode={triggerMode}
        triggerIcon={triggerIcon}
        onOpenChange={onListOpenChange}
      />
      {unavailableMessage != null && unavailableMessage !== "" ? (
        <p className="text-xs text-rose-700">{unavailableMessage}</p>
      ) : null}
    </div>
  );
}
